// OTPVerifyForm.js — axios version

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { SectionProps } from '../../utils/SectionProps';
import SectionHeader from './partials/SectionHeader';
import Input from '../elements/Input';
import Button from '../elements/Button';
import { Link, useLocation } from 'react-router-dom';
import { API_ENDPOINTS } from '../../api/endpoints';
import api from '../../api/http';
import { TokenService } from '../../api/tokenService';
import { ERROR_MESSAGES } from '../../api/errorMsg';

class OTPVerifyForm extends React.Component {
    constructor(props) {
        super(props);
        const emailFromLocation =
            (props.location &&
                props.location.state &&
                props.location.state.email) ||
            '';

        this.state = {
            email: emailFromLocation || props.defaultEmail || '',
            code: '',
            loading: false,
            error: '',
            success: '',
            timeLeft: props.initialCountdown || 60,
            disabledResend: true,
            showCongrats: false, // 🎉 popup after OTP success
        };

        this.timer = null;
        this.controller = null;
    }

    componentDidMount() {
        if (this.state.disabledResend) this.startTimer();
    }

    componentWillUnmount() {
        this.clearTimer();
        if (this.controller) this.controller.abort();
    }

    startTimer = () => {
        this.clearTimer();
        const initial = this.props.initialCountdown || 60;
        this.setState({ timeLeft: initial, disabledResend: true }, () => {
            this.timer = setInterval(() => {
                this.setState((s) => {
                    if (s.timeLeft <= 1) {
                        this.clearTimer();
                        return { timeLeft: 0, disabledResend: false };
                    }
                    return { timeLeft: s.timeLeft - 1 };
                });
            }, 1000);
        });
    };

    clearTimer = () => {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    };

    handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'code') {
            const v = value.replace(/\D/g, '').slice(0, 8);
            this.setState({ code: v, error: '', success: '' });
        } else {
            this.setState({ [name]: value, error: '', success: '' });
        }
    };

    // ⭐ axios version – match Swagger: { email, code }
    defaultVerify = async (email, code, signal) => {
        if (!API_ENDPOINTS.VERIFY_EMAIL) {
            throw new Error('VERIFY_EMAIL endpoint not configured.');
        }
        const res = await api.post(
            API_ENDPOINTS.VERIFY_EMAIL,
            {
                email,
                code, // 👈 field name must be "code"
            },
            { signal }
        );
        return res.data;
    };

    defaultResend = async () => {
        const { email } = this.state;

        if (!email) {
            throw new Error(ERROR_MESSAGES.OTP.INVALID_EMAIL);
        }
        if (!API_ENDPOINTS.RESEND_OTP) {
            throw new Error('RESEND_OTP endpoint not configured.');
        }

        // POST /api/v1/accounts/resend-otp { email }
        const res = await api.post(API_ENDPOINTS.RESEND_OTP, { email });
        return res.data;
    };

    handleResend = async () => {
        this.setState({ loading: true, error: '', success: '' });
        try {
            await Promise.resolve(this.defaultResend());
            this.setState({
                success: ERROR_MESSAGES.OTP.RESEND_SUCCESS,
                code: '',
            });
            this.startTimer();
        } catch (err) {
            let msg = ERROR_MESSAGES.OTP.RESEND_FAILED;

            if (err.response) {
                const backendMsg =
                    err.response?.data?.message || err.response?.data?.error;
                if (backendMsg) {
                    msg = backendMsg;
                }
            } else if (err.message === 'Network Error') {
                msg = ERROR_MESSAGES.OTP.NETWORK_ERROR;
            }

            this.setState({ error: msg });
        } finally {
            this.setState({ loading: false });
        }
    };

    handleSubmit = async (e) => {
        e && e.preventDefault();
        const { code, email } = this.state;
        const { onVerify, showEmailField = true, autoRedirect = false } =
            this.props;

        if (showEmailField && !email) {
            this.setState({ error: ERROR_MESSAGES.OTP.INVALID_EMAIL });
            return;
        }
        if (!code || code.length < (this.props.minCodeLength || 4)) {
            this.setState({ error: ERROR_MESSAGES.OTP.INVALID_CODE });
            return;
        }

        this.setState({ loading: true, error: '', success: '' });
        this.controller = new AbortController();
        const signal = this.controller.signal;

        try {
            let result;
            if (typeof onVerify === 'function') {
                result = await Promise.resolve(
                    onVerify(showEmailField ? email : undefined, code, signal)
                );
            } else {
                result = await this.defaultVerify(email, code, signal);
            }

            // 🔑 read tokens from verify response
            const accessToken =
                result?.accessToken ||
                result?.data?.accessToken ||
                result?.tokens?.accessToken;

            const refreshToken =
                result?.refreshToken ||
                result?.data?.refreshToken ||
                result?.tokens?.refreshToken;

            if (accessToken || refreshToken) {
                if (TokenService && typeof TokenService.setTokens === 'function') {
                    TokenService.setTokens(accessToken || '', refreshToken || '');
                }
                if (accessToken) {
                    localStorage.setItem('accessToken', accessToken);
                }
                if (refreshToken) {
                    localStorage.setItem('refreshToken', refreshToken);
                }
            }

            const token =
                (typeof result === 'string' && result) ||
                (result && (result.accessToken || result.token)) ||
                null;

            if (typeof this.props.onVerified === 'function') {
                try {
                    this.props.onVerified(token || result);
                } catch (e) { }
            }

            if (autoRedirect) {
                this.setState({ success: ERROR_MESSAGES.OTP.VERIFIED_SUCCESS });

                const query = token ? `?token=${encodeURIComponent(token)}` : '';
                try {
                    if (this.props.history?.push) {
                        this.props.history.push(`/reset-password${query}`);
                    } else {
                        window.location.href = `/reset-password${query}`;
                    }
                } catch {
                    window.location.href = `/reset-password${query}`;
                }
            } else {
                // Signup flow: show Congratulations popup
                this.setState({
                    success: ERROR_MESSAGES.OTP.VERIFIED_SUCCESS,
                    showCongrats: true,
                });
            }
        } catch (err) {
            if (err.name === 'CanceledError' || err.name === 'AbortError') {
                console.log('verify aborted');
            } else {
                let msg = ERROR_MESSAGES.OTP.DEFAULT;

                if (err.response) {
                    const status = err.response.status;
                    const backendMsg =
                        err.response?.data?.message || err.response?.data?.error;

                    if (backendMsg) {
                        msg = backendMsg;
                    } else if (status === 400 || status === 401) {
                        msg = ERROR_MESSAGES.OTP.EXPIRED_OR_INCORRECT;
                    }
                } else if (err.message === 'Network Error') {
                    msg = ERROR_MESSAGES.OTP.NETWORK_ERROR;
                }

                this.setState({ error: msg });
            }
        } finally {
            this.setState({ loading: false });
            this.controller = null;
        }
    };

    handleCongratsOk = () => {
        this.setState({ showCongrats: false }, () => {
            try {
                localStorage.setItem('tutorialStep', '1'); // Start tutorial for new user
                if (this.props.history && this.props.history.push) {
                    this.props.history.push('/login');
                } else {
                    window.location.href = '/login';
                }
            } catch {
                window.location.href = '/login';
            }
        });
    };

    render() {
        const {
            className,
            topOuterDivider,
            bottomOuterDivider,
            topDivider,
            bottomDivider,
            hasBgColor,
            invertColor,
            methodLabel = 'メールアドレス',
            showEmailField = true,
            ...props
        } = this.props;

        const {
            code,
            loading,
            error,
            success,
            disabledResend,
            timeLeft,
            email,
            showCongrats,
        } = this.state;

        const outerClasses = classNames(
            'signin section',
            topOuterDivider && 'has-top-divider',
            bottomOuterDivider && 'has-bottom-divider',
            hasBgColor && 'has-bg-color',
            invertColor && 'invert-color',
            className
        );

        const innerClasses = classNames(
            'signin-inner section-inner',
            topDivider && 'has-top-divider',
            bottomDivider && 'has-bottom-divider'
        );

        const sectionHeader = {
            title: '確認コードを入力',
            paragraph: `送信先: ${methodLabel}`,
        };

        return (
            <section {...props} className={outerClasses}>
                <div className="container">
                    <div className={innerClasses}>
                        <SectionHeader
                            tag="h2"
                            data={sectionHeader}
                            className="center-content"
                        />

                        <div className="tiles-wrap">
                            <div className="tiles-item">
                                <div className="tiles-item-inner">
                                    <form onSubmit={this.handleSubmit}>
                                        <fieldset>
                                            {showEmailField && (
                                                <div className="mb-12">
                                                    <Input
                                                        name="email"
                                                        type="email"
                                                        label="メールアドレス"
                                                        placeholder="メールアドレス"
                                                        labelHidden
                                                        value={email}
                                                        onChange={this.handleChange}
                                                        required
                                                        readOnly
                                                        disabled
                                                    />
                                                </div>
                                            )}

                                            <div className="mb-12">
                                                <Input
                                                    type="text"
                                                    name="code"
                                                    label="確認コード"
                                                    placeholder="確認コードを入力"
                                                    labelHidden
                                                    value={code}
                                                    onChange={this.handleChange}
                                                    required
                                                />
                                            </div>

                                            {error && (
                                                <div className="text-color-danger mb-12 text-xs">
                                                    {error}
                                                </div>
                                            )}
                                            {success && (
                                                <div className="text-color-success mb-12 text-xs">
                                                    {success}
                                                </div>
                                            )}

                                            <div className="mt-24 mb-32">
                                                <Button
                                                    type="submit"
                                                    color="primary"
                                                    wide
                                                    disabled={loading}
                                                >
                                                    {loading ? '検証中...' : 'コードを確認'}
                                                </Button>
                                            </div>

                                            <div className="signin-footer mb-32">
                                                <div className="footer-left">
                                                    <button
                                                        type="button"
                                                        onClick={this.handleResend}
                                                        className="func-link text-xs"
                                                        disabled={disabledResend || loading}
                                                    >
                                                        再送信
                                                    </button>
                                                    {disabledResend && (
                                                        <span className="ml-2 text-xs">({timeLeft}s)</span>
                                                    )}
                                                </div>
                                                <div className="footer-right">
                                                    <Link to="/login/" className="func-link text-xs">
                                                        ログインに戻る
                                                    </Link>
                                                </div>
                                            </div>
                                        </fieldset>
                                    </form>

                                    <div className="signin-bottom has-top-divider">
                                        <div className="pt-32 text-xs center-content text-color-low">
                                            コードを受け取らない場合は迷惑メールフォルダを確認してください。
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 🎉 Congratulations popup after OTP verification */}
                {showCongrats && (
                    <div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            backgroundColor: 'rgba(0,0,0,0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999,
                        }}
                    >
                        <div
                            style={{
                                background: '#fff',
                                padding: '32px 40px',
                                borderRadius: '8px',
                                maxWidth: '420px',
                                textAlign: 'center',
                                boxShadow: '0 12px 30px rgba(0,0,0,0.2)',
                            }}
                        >
                            <h2 style={{ marginBottom: '16px' }}>おめでとうございます！</h2>
                            <p style={{ marginBottom: '24px', fontSize: '14px' }}>
                                メールアドレスの確認が完了しました。
                                <br />
                                無料トライアルのクレジットをご利用いただけます。
                                <br />
                                ホームページへ移動します。
                            </p>
                            <Button onClick={this.handleCongratsOk}>OK</Button>
                        </div>
                    </div>
                )}
            </section>
        );
    }
}

OTPVerifyForm.propTypes = {
    ...SectionProps.types,
    methodLabel: PropTypes.string,
    onVerify: PropTypes.func,
    onResend: PropTypes.func,
    defaultEmail: PropTypes.string,
    initialCountdown: PropTypes.number,
    showEmailField: PropTypes.bool,
    autoRedirect: PropTypes.bool,
    onVerified: PropTypes.func,
    minCodeLength: PropTypes.number,
};

OTPVerifyForm.defaultProps = {
    ...SectionProps.defaults,
    methodLabel: 'メールアドレス',
    onVerify: null,
    onResend: null,
    defaultEmail: '',
    initialCountdown: 60,
    showEmailField: true,
    autoRedirect: false,
    onVerified: null,
    minCodeLength: 4,
};

// 👇 wrapper to inject location into class component
function OTPVerifyFormWithLocation(props) {
    const location = useLocation();
    return <OTPVerifyForm {...props} location={location} />;
}

export default OTPVerifyFormWithLocation;
